import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import axios from 'axios';
import { formatPrice } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import { useUserStore } from '../../store';

/**
 * "Tilaa <toimittaja>" for one source: a work order (`maintenance`, posts to the
 * integration's order endpoint) or a lähete (`partsSale`, posts to the sale's
 * order_from_supplier action with the given line ids). `unorderedParts` rows carry
 * `id`, `name`, `primary_code`/`manufacturer_code`, `quantity` and `cost_price`.
 */
function SupplierOrderDialog({
  open, onClose, maintenance, partsSale, integrationId, integrationName, unorderedParts, onOrderPlaced,
}) {
  const { t } = useTranslation();
  const isPartsSale = !!partsSale;
  const { user, currentOrganization } = useUserStore();
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // The part rows only carry the integration id; the type and the order email (for a
  // sähköpostitilaus) come from the integration itself.
  const [integration, setIntegration] = useState(null);
  const isEmail = integration?.supplier_type === 'email';

  useEffect(() => {
    if (!open || !integrationId) return;
    let cancelled = false;
    axios.get(`/api/suppliers/${integrationId}/`)
      .then((res) => { if (!cancelled) setIntegration(res.data); })
      .catch(() => { if (!cancelled) setIntegration(null); });
    return () => { cancelled = true; };
  }, [open, integrationId]);

  const handleClose = () => {
    setComment('');
    setError(null);
    setResult(null);
    onClose();
  };

  const total = unorderedParts.reduce((sum, p) => {
    const price = parseFloat(p.cost_price) || 0;
    const qty = parseFloat(p.quantity) || 0;
    return sum + (price * qty);
  }, 0);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = isPartsSale
        ? await axios.post(`/api/parts-sales/${partsSale.id}/order_from_supplier/`, {
            integration_id: integrationId,
            line_ids: unorderedParts.map((p) => p.id),
            comment,
          })
        : await axios.post(`/api/suppliers/${integrationId}/order/`, {
            maintenance_id: maintenance.id,
            comment,
          });
      setResult(response.data);
      if (onOrderPlaced) await onOrderPlaced();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (!err.response) {
        // Network error — no response at all
        setError(t('supplierOrder.connectionError'));
      } else if (status === 502) {
        setError(data?.error || t('supplierOrder.connectionError'));
      } else if (data?.error) {
        setError(data.error);
      } else {
        setError(t('supplierOrder.orderFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        }
      }}
    >
      {result ? (
        <>
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {result.email_delivery?.to
                ? t('supplierOrder.emailOrderSuccess', { email: result.email_delivery.to })
                : t('supplierOrder.orderSuccess')}
            </Typography>
            {result.order_reference && (
              <Typography sx={{ color: 'text.secondary' }}>
                {t('supplierOrder.orderReference')}: {result.order_reference}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none' }}>
              {t('Close')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
              {t('supplierOrder.dialogTitle', { supplier: integrationName })}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              {isPartsSale ? (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('supplierOrder.deliveryNote')}</Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>#{partsSale.number}</Typography>
                  </Box>
                  {partsSale.customer_name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('supplierOrder.customer')}</Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{partsSale.customer_name}</Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('supplierOrder.workOrder')}</Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{maintenance?.work_number}</Typography>
                  </Box>
                  {maintenance?.vehicle?.registration_number && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('supplierOrder.registrationNumber')}</Typography>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{maintenance.vehicle.registration_number}</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Parts table */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, px: 0.5 }}>
                <Typography sx={{ flex: 1, fontSize: '11px', color: 'text.secondary', fontWeight: 600 }}>
                  {t('supplierOrder.partName')}
                </Typography>
                <Typography sx={{ width: 100, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
                  {t('supplierOrder.articleCode')}
                </Typography>
                <Typography sx={{ width: 50, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
                  {t('supplierOrder.quantity')}
                </Typography>
                <Typography sx={{ width: 80, fontSize: '11px', color: 'text.secondary', fontWeight: 600, textAlign: 'right' }}>
                  {t('supplierOrder.purchasePrice')}
                </Typography>
              </Box>
              {unorderedParts.map((part) => (
                <Box key={part.id} sx={{
                  display: 'flex', gap: 1, py: 0.75, px: 0.5,
                  borderBottom: 1, borderColor: 'divider',
                  alignItems: 'center',
                }}>
                  <Typography sx={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>
                    {part.name}
                  </Typography>
                  <Typography sx={{ width: 100, fontSize: '12px', color: 'text.secondary', textAlign: 'right' }}>
                    {part.primary_code || part.manufacturer_code || '-'}
                  </Typography>
                  <Typography sx={{ width: 50, fontSize: '13px', textAlign: 'right' }}>
                    {formatQuantity(part.quantity)}
                  </Typography>
                  <Typography sx={{ width: 80, fontSize: '13px', textAlign: 'right' }}>
                    {formatPrice(part.cost_price)} €
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                  {t('supplierOrder.totalExclVat')}
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatPrice(total)} €
                </Typography>
              </Box>
            </Box>

            {isEmail && (
              <Alert severity="info" icon={<EmailOutlinedIcon fontSize="inherit" />} sx={{ mb: 1.5 }} data-testid="email-order-info">
                <Typography sx={{ fontSize: '13px' }}>
                  {t('supplierOrder.emailRecipient', { email: integration.order_email })}
                </Typography>
                {user?.email && (
                  <Typography sx={{ fontSize: '13px' }}>
                    {t('supplierOrder.emailConfirmation', { email: user.email })}
                  </Typography>
                )}
                {currentOrganization?.lahete_inbox_address && (
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                    {t('supplierOrder.emailLahete', { address: currentOrganization.lahete_inbox_address })}
                  </Typography>
                )}
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label={isEmail ? t('supplierOrder.messageToSupplier') : t('supplierOrder.comment')}
              placeholder={isEmail ? t('supplierOrder.messagePlaceholder') : t('supplierOrder.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} sx={{ borderRadius: '8px', textTransform: 'none' }}>
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="supplier-order-submit"
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
            >
              {loading ? (
                <>{t('supplierOrder.submitting')} <CircularProgress size={16} sx={{ ml: 1 }} /></>
              ) : (
                t('supplierOrder.submitOrder')
              )}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default SupplierOrderDialog;
